import React, { useEffect, useMemo, useState } from "react";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import PageHeader from "../components/common/PageHeader";
import Button from "../components/common/Button";
import useStore from "../store/useStore";
import api from "../services/api";

const COMPANY_COLUMNS = [
  "companyName",
  "email",
  "phone",
  "address",
  "contactPerson",
  "subscriptionStatus",
  "subscriptionPlan",
  "subscriptionCycle",
  "subscriptionStart",
  "subscriptionEnd",
  "subscriptionAmount",
  "subscriptionCurrency",
  "paymentContactMethod",
  "active",
  "adminFirstName",
  "adminLastName",
  "adminEmail",
  "adminPassword"
];

const RECORD_COLUMNS = {
  tenants: ["tenantName", "phone", "email", "address", "companyName"],
  "asset-owners": ["firstName", "lastName", "phone", "email", "address", "companyName"],
  assets: ["assetName", "location", "category", "assetNumber", "rentalSpace", "ownerName", "description", "companyName"],
  rentals: ["assetName", "unitNumber", "rentalCost", "rentalType", "billingPeriod", "occupied", "tenantName", "startDate", "endDate", "description", "companyName"],
  payments: ["unitNumber", "amount", "currency", "paymentDate", "paymentMethod", "receivedBy", "notes", "companyName"],
  maintenance: ["assetName", "cost", "date", "maintenanceType", "performedBy", "status", "description", "companyName"],
  users: ["firstName", "lastName", "email", "role", "password", "companyName"]
};

const STAFF_MODULES = ["tenants", "asset-owners", "assets", "rentals", "payments", "maintenance"];

const ImportRecords = () => {
  const { user, authRole, toast } = useStore();
  const role = authRole || user?.role;
  const isSuperAdmin = role === "SuperAdmin";
  const isStaff = role === "Staff";
  const [moduleName, setModuleName] = useState(isSuperAdmin ? "companies" : "tenants");
  const [file, setFile] = useState(null);
  const [companyId, setCompanyId] = useState("");
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [result, setResult] = useState(null);

  const canImport = role === "SuperAdmin" || role === "CompanyAdmin" || role === "Staff";
  const isCompaniesModule = moduleName === "companies";

  const moduleOptions = useMemo(() => {
    if (isSuperAdmin) return { companies: COMPANY_COLUMNS, ...RECORD_COLUMNS };
    if (isStaff) {
      return Object.fromEntries(
        STAFF_MODULES.map((module) => [module, RECORD_COLUMNS[module]])
      );
    }
    return RECORD_COLUMNS;
  }, [isSuperAdmin, isStaff]);

  const moduleColumns = (moduleOptions[moduleName] || []).filter((column) => (
    isSuperAdmin || isCompaniesModule || column !== "companyName"
  ));

  useEffect(() => {
    if (!isSuperAdmin) return undefined;

    const loadCompanies = async () => {
      try {
        const res = await api.get("/companies");
        setCompanies(res.data?.data || []);
      } catch (error) {
        console.error("Failed to load companies for import:", error);
      }
    };

    loadCompanies();
  }, [isSuperAdmin]);

  const handleDownloadTemplate = () => {
    const headers = isCompaniesModule
      ? COMPANY_COLUMNS
      : (isSuperAdmin ? moduleColumns : moduleColumns.filter((column) => column !== "companyName"));
    const csv = `${headers.join(",")}\n`;
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${moduleName}-template.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const params = {};
      if (isSuperAdmin && !isCompaniesModule && companyId) {
        params.companyId = companyId;
      }

      const res = await api.get(`/imports/${moduleName}/export`, {
        params,
        responseType: "blob"
      });
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `${moduleName}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
      toast("Export downloaded", "success");
    } catch (error) {
      let message = "Export failed";
      const data = error.response?.data;
      if (data instanceof Blob) {
        try {
          const parsed = JSON.parse(await data.text());
          message = parsed.message || message;
        } catch (parseError) {
          message = "Export failed";
        }
      } else if (data?.message) {
        message = data.message;
      }
      toast(message, "error");
    } finally {
      setExporting(false);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast("Please select an Excel file first", "error");
      return;
    }

    try {
      setLoading(true);
      setResult(null);

      const formData = new FormData();
      formData.append("file", file);
      if (isSuperAdmin && !isCompaniesModule && companyId) {
        formData.append("companyId", companyId);
      }

      const res = await api.post(`/imports/${moduleName}`, formData);
      setResult(res.data.data);
      toast(res.data?.message || "Import completed", "success");
    } catch (error) {
      const message = error.response?.data?.message || "Import failed";
      toast(message, "error");
      setResult({
        imported: 0,
        failed: 0,
        errors: [{ row: "-", message }]
      });
    } finally {
      setLoading(false);
    }
  };

  if (!canImport) {
    return (
      <div>
        <PageHeader title="Import & Export" />
        <div className="bg-white border border-gray-200 rounded-xl p-6 text-sm text-gray-700">
          You do not have permission to access this feature.
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader title="Import & Export" />
      <p className="text-sm text-gray-600 mb-6">
        {isStaff
          ? "Import or download tenants, owners, assets, rentals, payments, and maintenance for your company. User accounts stay with a Company Admin."
          : "Upload a spreadsheet or download the records already stored for the selected module."}
      </p>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-6">
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Module</label>
            <select
              value={moduleName}
              onChange={(e) => {
                setModuleName(e.target.value);
                setResult(null);
              }}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-md bg-white focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
            >
              {Object.keys(moduleOptions).map((module) => (
                <option key={module} value={module}>{module}</option>
              ))}
            </select>
          </div>

          {isSuperAdmin && !isCompaniesModule && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Target Company</label>
              <select
                value={companyId}
                onChange={(e) => setCompanyId(e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-md bg-white focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
              >
                <option value="">Use companyName column in Excel</option>
                {companies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.companyName} (ID {company.id})
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Excel File</label>
            <input
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-md bg-white"
            />
            <p className="text-xs text-gray-500 mt-2">First sheet will be imported. Header row must match the columns below. Max size: 10MB.</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-3 mt-6">
          <Button variant="primary" onClick={handleUpload} disabled={loading || exporting}>
            {loading ? "Importing..." : "Upload & Import"}
          </Button>
          <Button variant="secondary" onClick={handleExport} disabled={loading || exporting}>
            {exporting ? "Exporting..." : "Export Current Records"}
          </Button>
          <Button variant="secondary" onClick={handleDownloadTemplate} disabled={loading || exporting}>
            Download CSV Template
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-6">
        <div className="flex items-center gap-2 text-gray-800 mb-3">
          <UploadFileOutlinedIcon sx={{ fontSize: 18 }} />
          <h3 className="text-sm font-semibold">Expected Columns for {moduleName}</h3>
        </div>
        <div className="flex flex-wrap gap-2">
          {moduleColumns.map((col) => (
            <span key={col} className="px-2 py-1 text-xs bg-teal-50 text-teal-700 rounded border border-teal-100">
              {col}
            </span>
          ))}
        </div>
        {isCompaniesModule ? (
          <p className="text-xs text-gray-500 mt-3">
            Required: companyName and email. Optional adminFirstName, adminLastName, and adminEmail create a CompanyAdmin for that company. Spaces in headers are allowed, for example Company Name.
          </p>
        ) : (
          <p className="text-xs text-gray-500 mt-3">
            {isSuperAdmin
              ? "Select a target company above, or include a companyName column so each row is assigned to the matching company."
              : "Rows are added to your company. Export downloads the records already stored for that module."}
          </p>
        )}
        {moduleName === "users" && (
          <p className="text-xs text-gray-500 mt-2">
            Users sign in with email. Password is optional in the spreadsheet and will be generated if omitted. Exported user files do not include passwords.
          </p>
        )}
        {["assets", "rentals", "payments", "maintenance"].includes(moduleName) && (
          <p className="text-xs text-gray-500 mt-2">
            You can also match existing records with ownerId, assetId, tenantId, or rentalId.
          </p>
        )}
      </div>

      {result && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">Import Summary</h3>
          <div className="grid sm:grid-cols-3 gap-3 mb-4">
            <div className="rounded-lg border border-gray-200 p-3">
              <p className="text-xs text-gray-500">Total Rows</p>
              <p className="text-lg font-semibold text-gray-900">{result.totalRows || 0}</p>
            </div>
            <div className="rounded-lg border border-green-200 bg-green-50 p-3">
              <p className="text-xs text-green-700">Imported</p>
              <p className="text-lg font-semibold text-green-800">{result.imported || 0}</p>
            </div>
            <div className="rounded-lg border border-red-200 bg-red-50 p-3">
              <p className="text-xs text-red-700">Failed</p>
              <p className="text-lg font-semibold text-red-800">{result.failed || 0}</p>
            </div>
          </div>

          {!!result.errors?.length && (
            <div>
              <p className="text-sm font-medium text-gray-800 mb-2">Row Errors</p>
              <div className="max-h-56 overflow-auto border border-gray-200 rounded-md">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium">Row</th>
                      <th className="text-left px-3 py-2 font-medium">Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.errors.map((err, idx) => (
                      <tr key={`${err.row}-${idx}`} className="border-b border-gray-100 last:border-0">
                        <td className="px-3 py-2 text-gray-600">{err.row}</td>
                        <td className="px-3 py-2 text-gray-800">{err.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ImportRecords;
